import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Camera, Loader2, Upload, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

/**
 * ReceiptScanner — small inline "Scan Receipt" button that triggers a file
 * picker, sends the image to /api/finance/ocr-receipt, and calls
 * onExtracted(fields) so the parent dialog can pre-fill its form.
 *
 * Props:
 *   api       – axios instance from useAuth
 *   kind      – 'ap' | 'expense' (sent to the backend for schema-specific extraction)
 *   onExtracted(fields) – called with the parsed dict
 */
export default function ReceiptScanner({ api, kind, onExtracted }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const triggerPicker = () => fileInputRef.current?.click();

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error('Only JPG, PNG, or WEBP images supported'); return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large — keep under 8 MB'); return;
    }
    setBusy(true); setDone(false);
    try {
      const dataUrl = await toBase64(file);
      const { data } = await api.post('/finance/ocr-receipt', { image_base64: dataUrl, kind });
      const fields = data?.fields || {};
      if (Object.keys(fields).length === 0) {
        toast.warning('Could not detect receipt fields — please fill manually');
      } else {
        onExtracted?.(fields);
        setDone(true);
        toast.success('Receipt scanned — review the fields before saving');
        setTimeout(() => setDone(false), 3000);
      }
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Scan failed — try again');
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid="receipt-file-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={triggerPicker}
        disabled={busy}
        className="gap-1.5 h-9 border-[#6B3FA0] text-[#6B3FA0] hover:bg-[#EDE7F6]"
        data-testid="scan-receipt-btn"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
          done ? <CheckCircle2 className="h-3.5 w-3.5 text-[#2E7D32]" /> :
            <Camera className="h-3.5 w-3.5" />}
        {busy ? 'Scanning…' : done ? 'Scanned!' : 'Scan Receipt'}
      </Button>
      <span className="text-[10px] text-[#8E85A0]">
        <Upload className="h-2.5 w-2.5 inline mr-0.5" />
        AI auto-fills vendor, amount, date & more
      </span>
    </div>
  );
}
